/**
 * Pure helpers for staging bidder-visible config edits on a published or
 * open auction. Extracted from the configure store so they're testable
 * without Pinia. See docs/superpowers/specs/2026-06-12-publish-flow-design.md
 * (Admin-web adaptation §2, §6).
 */
import { utcIsoToAuctionTzDate } from '@/shell';
import { LEGACY_STARTING_BID_PERCENT } from '@neon-one/auctions-types';
import type { AuctionEvent } from '@neon-one/auctions-types';

import type { StageableField } from '@/composables/usePendingEdits';
import type { DraftState } from './store';

/** Wire-payload key for each stageable field (most map 1:1). */
const WIRE_KEY: Record<StageableField, string> = {
  name: 'name',
  descriptionHtml: 'descriptionHtml',
  auctionOpenAt: 'auctionOpenAt',
  auctionCloseAt: 'auctionCloseAt',
  defaultIncrement: 'defaultBidIncrementCents',
  defaultIncrementPercent: 'defaultBidIncrementPercent',
  defaultStartingBidPercent: 'defaultStartingBidPercent',
  defaultStartingBidFixed: 'defaultStartingBidCents',
  proxyBidding: 'proxyBidding',
  termsAndConditionsEnabled: 'termsAndConditionsEnabled',
  termsAndConditionsHtml: 'termsAndConditionsHtml',
};

/**
 * Mirror of draftToPayload's cents math so the diff agrees with the payload.
 *
 * Every money comparison below runs through this. Draft state carries dollars
 * for the currency inputs, so comparing those dollars directly against a
 * cents-valued event field would be a float equality test on a value the
 * payload rounds anyway — compare what will actually be sent.
 */
function moneyToCents(dollars: number): number {
  return Math.max(1, Math.round(dollars * 100));
}

/** True when two ISO strings name the same instant (ignores format/precision). */
function sameInstant(a: string, b: string): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  if (Number.isNaN(ta) || Number.isNaN(tb)) return a === b;
  return ta === tb;
}

/**
 * The stageable fields whose draft value differs from the published event.
 * Increment is compared in cents (draft carries dollars); dates compare by
 * instant; everything else is a direct compare.
 */
export function stagedDiff(draft: DraftState, event: AuctionEvent): StageableField[] {
  const changed: StageableField[] = [];
  if (draft.name !== event.name) changed.push('name');
  if (draft.descriptionHtml !== event.descriptionHtml) changed.push('descriptionHtml');
  if (!sameInstant(draft.auctionOpenAt, event.auctionOpenAt)) changed.push('auctionOpenAt');
  if (!sameInstant(draft.auctionCloseAt, event.auctionCloseAt)) changed.push('auctionCloseAt');
  if (moneyToCents(draft.defaultIncrement) !== event.defaultBidIncrementCents) {
    changed.push('defaultIncrement');
  }
  if ((draft.defaultIncrementPercent ?? null) !== (event.defaultBidIncrementPercent ?? null)) {
    changed.push('defaultIncrementPercent');
  }
  if (draft.defaultStartingBidPercent !== (event.defaultStartingBidPercent ?? LEGACY_STARTING_BID_PERCENT)) {
    changed.push('defaultStartingBidPercent');
  }
  const draftStartFixed =
    draft.defaultStartingBidFixed != null ? moneyToCents(draft.defaultStartingBidFixed) : null;
  if (draftStartFixed !== (event.defaultStartingBidCents ?? null)) {
    changed.push('defaultStartingBidFixed');
  }
  if (draft.proxyBidding !== event.proxyBidding) changed.push('proxyBidding');
  if (draft.termsAndConditionsEnabled !== event.termsAndConditionsEnabled) {
    changed.push('termsAndConditionsEnabled');
  }
  if (draft.termsAndConditionsHtml !== (event.termsAndConditionsHtml ?? '')) {
    changed.push('termsAndConditionsHtml');
  }
  return changed;
}

/**
 * Return a copy of `payload` with the staged fields' wire keys removed, so
 * the autosave PATCH doesn't write bidder-visible edits through immediately.
 */
export function filterStagedKeys<T extends Record<string, unknown>>(
  payload: T,
  staged: StageableField[]
): Partial<T> {
  if (staged.length === 0) return { ...payload };
  const drop = new Set(staged.map((f) => WIRE_KEY[f]));
  return Object.fromEntries(
    Object.entries(payload).filter(([k]) => !drop.has(k))
  ) as Partial<T>;
}

/** Strip the narrow / non-breaking spaces modern ICU injects before AM/PM. */
function normalizeSpaces(s: string): string {
  return s.replace(/[\u202f\u00a0]/g, ' ');
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

/**
 * Human-readable display string for a staged field's value, used on the
 * pending-edits card rows. Dates are rendered in the auction timezone.
 */
export function displayFor(field: StageableField, value: unknown, timezone: string): string {
  switch (field) {
    case 'defaultIncrement':
    case 'defaultStartingBidFixed':
      return value == null ? 'Not set' : `$${value}`;
    case 'defaultIncrementPercent':
    case 'defaultStartingBidPercent':
      return value == null ? 'Fixed amount' : `${value}% of value`;
    case 'proxyBidding':
    case 'termsAndConditionsEnabled':
      return value ? 'On' : 'Off';
    case 'auctionOpenAt':
    case 'auctionCloseAt': {
      const iso = typeof value === 'string' ? value : '';
      if (!iso) return 'Not set';
      const d = utcIsoToAuctionTzDate(iso, timezone);
      if (!d) return 'Not set';
      return normalizeSpaces(
        d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
          ' ' +
          d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      );
    }
    default: {
      const s = stripHtml(String(value ?? ''));
      return s.length > 40 ? `${s.slice(0, 40)}…` : s;
    }
  }
}
